import * as React from 'react';
import { MoreActionsButton } from './MoreActionsButton';
import { HStack, VStack } from '@chakra-ui/react';
import { AcceptActionButton, ActionButton, ActionButtonDefinition, BackActionButton, ButtonSurface, CancelActionButton, NullActionButton } from './ActionButton';
import { Filler } from './Filler';


type Props = {
	buttons: Nullable<ActionButtonDefinition>[],
	removeCancelButton?: boolean;
	replaceCancelByBackButton?: boolean;
	extraButtons?: { rows: number, columns: number, buttons: Nullable<ActionButtonDefinition>[] },
	surface: ButtonSurface,
};

export function isCancelButton(btn: Nullable<ActionButtonDefinition>) {
	return btn?.intent === 'cancel';
}

export function isBackButton(btn: Nullable<ActionButtonDefinition>) {
	return btn?.intent === 'back';
}

export function isAcceptButton(btn: Nullable<ActionButtonDefinition>) {
	return btn?.intent === 'ok';
}

export function isMoreOptionsButton(btn: Nullable<ActionButtonDefinition>) {
	return btn?.intent === 'more-options';
}

export function HQuickActionsPanel({ buttons, surface, extraButtons, removeCancelButton, replaceCancelByBackButton }: Props) {

	const moreOptions = extraButtons ?? { rows: 0, columns: 0, buttons: [] };

	if (buttons.some(isAcceptButton) && !isAcceptButton(buttons.last()))
		throw new Error('El botón más a la derecha debería ser un botón de Aceptar');

	if (buttons.some(isCancelButton) && !isCancelButton(buttons[0]))
		throw new Error('El botón más a la izquierda debería ser un botón de Volver/Cancelar');


	const actionButtons = removeCancelButton ? buttons.filter(x => !isCancelButton(x)) : buttons;
	const hasMoreOptionsButton = moreOptions.buttons.some(x => x !== null);

	// Si no tengo botones no pinto nada
	const allAreNullButtons = actionButtons.every(x => x === null);
	if (allAreNullButtons && !hasMoreOptionsButton)
		return null;

	return <HStack w={'full'} justifyContent={allAreNullButtons ? 'end' : 'space-between'}>
		{
			actionButtons.map((btn, idx) => {

				if (btn === null) {
					return <Filler key={idx} />;
				}

				if (isAcceptButton(btn)) {
					return <AcceptActionButton key={idx} surface={surface} action={btn.action} />;
				}

				if (isCancelButton(btn)) {
					return replaceCancelByBackButton ? <BackActionButton key={idx} surface={surface} action={btn.action} /> : <CancelActionButton key={idx} surface={surface} action={btn.action} />;
				}

				if (isBackButton(btn)) {
					return <BackActionButton key={idx} surface={surface} action={btn.action} />;
				}

				return <ActionButton key={idx} {...btn} surface={surface} />;
			})
		}
		{hasMoreOptionsButton ? <MoreActionsButton rows={moreOptions.rows} columns={moreOptions.columns} buttons={moreOptions.buttons} surface={surface} /> : null}
	</HStack>;
}


type VQuickActionsPanelProps = {
	buttons: Nullable<ActionButtonDefinition>[],
	columns: number,
	rows: number,
	extraButtons?: { rows: number, columns: number, buttons: Nullable<ActionButtonDefinition>[], variant?: ButtonSurface, },
	surface: ButtonSurface,
};

export function VQuickActionsPanel({ buttons, surface, extraButtons, rows }: VQuickActionsPanelProps) {

	const moreOptions = extraButtons ?? { rows: 0, columns: 0, buttons: [] };

	const hasMoreOptionsButton = moreOptions.buttons.some(x => x !== null);
	// Si no tengo botones no pinto nada
	const allAreNullButtons = buttons.every(x => x === null);
	if (allAreNullButtons && !hasMoreOptionsButton)
		return null;

	const buttonsWithMore = hasMoreOptionsButton
		? [...buttons, { __moreAction: true } as any]
		: buttons;

	// OJO: Los gaps, shrinks y demás están optimizados para cubrir los siguientes casos:
	// - height: 768 => 9 botones
	// - height: 768 => 10 botones (aplastados) para facilitar migración de TPV antiguo
	// - height: 894 => 10 botones
	// - height: 960 => 11 botones
	// - height 1090 => 13 botones

	// Creo un HStack con un VStack por cada columna.

	return <HStack gapX={'1.5'} height={'full'}>
		{buttonsWithMore.partition(rows).map((columnButtons, columnIndex) => {
			return <VStack
				key={columnIndex}
				h={'full'}
				maxW={'full'}
				flexShrink={0}
				overflowY={'hidden'}
				overflowX={'hidden'}
				justifyContent={'space-between'}
				alignItems={'center'}
				direction={'column'}
				wrap={'no-wrap'}
				w={'auto'}
				gapY={'0.5'}
				gapX={'1.5'}
				css={{
					/* Esto aplasta los botones y puede hacerlos "feos", pero permite que al actualizar
					   una versión antigua de Ágora no haya que hacer nada */
					'& > *': {
						flexShrink: 1,
					},
				}}>

				{columnButtons.map((btn, idx) =>
					btn === null || isMoreOptionsButton(btn)
						? <NullActionButton key={idx} surface={surface} />
						: btn.__moreAction
							? <MoreActionsButton
								key='more-actions'
								rows={moreOptions.rows}
								columns={moreOptions.columns}
								buttons={moreOptions.buttons}
								surface={surface}
							/>
							: <ActionButton key={idx} {...btn} surface={surface} />
				)}
			</VStack>;
		})}
	</HStack>;
}